import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { apiClient } from "@finitless/shared/lib";
import { toast } from "sonner";

export const useUpdateAddressDefault = () => {
  const { t } = useTranslation("customers");
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, addressId, default: isDefault }) => {
      const response = await apiClient.put(
        `/users/${userId}/addresses/${addressId}`,
        {
          default: isDefault,
        },
      );

      if (response.error) {
        const errorMessage = Array.isArray(response.result)
          ? response.result.join(", ")
          : response.result || t("toasts.addressUpdateFailedDescription");
        throw new Error(errorMessage);
      }

      return response.result || response;
    },
    onMutate: async ({ userId, addressId }) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: ["user", userId] });

      // Snapshot the previous value
      const previousUserData = queryClient.getQueryData(["user", userId]);

      // Optimistically update the cache
      queryClient.setQueryData(["user", userId], (oldData) => {
        if (
          !oldData ||
          !oldData.addresses ||
          !Array.isArray(oldData.addresses)
        ) {
          return oldData;
        }

        return {
          ...oldData,
          addresses: oldData.addresses.map((address) =>
            address.id === addressId
              ? { ...address, default: true }
              : { ...address, default: false },
          ),
        };
      });

      // Return context with the previous value for rollback
      return { previousUserData };
    },
    onSuccess: (data, variables) => {
      const { userId } = variables;

      // Invalidate to sync with server (but UI already updated optimistically)
      queryClient.invalidateQueries({ queryKey: ["user", userId] });

      toast.success(t("toasts.addressUpdatedTitle"), {
        description: t("toasts.addressUpdatedDescription"),
        duration: 3000,
      });
    },
    onError: (error, variables, context) => {
      const { userId } = variables;

      // Rollback to previous value on error
      if (context?.previousUserData) {
        queryClient.setQueryData(["user", userId], context.previousUserData);
      }

      toast.error(t("toasts.updateFailedTitle"), {
        description:
          error.message || t("toasts.addressUpdateFailedDescription"),
        duration: 3000,
      });
    },
  });
};

export default useUpdateAddressDefault;
